import { CELL, SPAN } from './constants';
import { cheb, cx, cy, wireLength } from './geometry';
import type { Edge, HouseLink, Network, NodeRole, Point, Puzzle } from './types';

/**
 * The wired network implied by a tower layout.
 *
 * Everything the board draws comes from here, and {@link statsFor} mirrors the
 * same rules for scoring — keep the two in step.
 */
export function network(puzzle: Puzzle, towers: Point[]): Network {
  const nodes: Point[] = [puzzle.plant, ...towers];

  const energized = new Set([0]);
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 1; i < nodes.length; i++) {
      if (energized.has(i)) continue;
      for (const j of energized) {
        if (cheb(nodes[i], nodes[j]) <= SPAN) {
          energized.add(i);
          changed = true;
          break;
        }
      }
    }
  }

  const edges: Edge[] = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (cheb(nodes[i], nodes[j]) <= SPAN) {
        edges.push({
          a: nodes[i],
          b: nodes[j],
          ai: i,
          bi: j,
          len: wireLength(nodes[i], nodes[j]),
          live: energized.has(i) && energized.has(j),
        });
      }
    }
  }

  // shortest wire-length from the plant to every energized node (drives the surge)
  const dist = Array<number>(nodes.length).fill(Infinity);
  dist[0] = 0;
  const liveEdges = edges.filter((e) => e.live);
  for (let it = 0; it < nodes.length; it++) {
    for (const e of liveEdges) {
      if (dist[e.ai] + e.len < dist[e.bi]) dist[e.bi] = dist[e.ai] + e.len;
      if (dist[e.bi] + e.len < dist[e.ai]) dist[e.ai] = dist[e.bi] + e.len;
    }
  }

  const houseLinks: HouseLink[] = puzzle.houses.map((h) => {
    let best: Point | null = null;
    let bestIdx: number | null = null;
    let bestD = 1e9;
    let lit = false;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (cheb(n, h) <= SPAN) {
        const d = (n.r - h.r) ** 2 + (n.c - h.c) ** 2;
        const e = energized.has(i);
        if (e && !lit) {
          lit = true;
          best = n;
          bestIdx = i;
          bestD = d;
        } else if (e === lit && d < bestD) {
          best = n;
          bestIdx = i;
          bestD = d;
        }
      }
    }
    const len = best ? wireLength(best, h) : 0;
    return {
      h,
      node: best,
      nodeIdx: bestIdx,
      lit,
      wireLen: len,
      arrive: lit && bestIdx !== null ? dist[bestIdx] + len : Infinity,
    };
  });

  return { nodes, energized, edges, dist, houseLinks };
}

/** Does the straight line between two cells pass over water? */
function spansWater(puzzle: Puzzle, p: Point, q: Point): boolean {
  for (let t = 1; t < 32; t++) {
    const x = cx(p) + (cx(q) - cx(p)) * (t / 32);
    const y = cy(p) + (cy(q) - cy(p)) * (t / 32);
    if (puzzle.obstacles[Math.floor(y / CELL) + ',' + Math.floor(x / CELL)] === 'water')
      return true;
  }
  return false;
}

/**
 * A through-line tower turns "sharply" when its two spans deviate more than
 * 45° from a straight run — the angle between the span vectors is under 135°.
 * Integer-exact: with dot = u·v, the angle is under 135° iff dot ≥ 0 (a
 * right-angle or tighter turn) or dot² doubled still falls short of |u|²|v|²
 * — so an exact 45° kink (2·dot² = |u|²|v|²) stays on a suspension tower.
 */
function sharpTurn(n: Point, a: Point, b: Point): boolean {
  const u = { r: a.r - n.r, c: a.c - n.c };
  const v = { r: b.r - n.r, c: b.c - n.c };
  const dot = u.r * v.r + u.c * v.c;
  if (dot >= 0) return true;
  return 2 * dot * dot < (u.r * u.r + u.c * u.c) * (v.r * v.r + v.c * v.c);
}

/**
 * Which sprite each node wears. A tower earns the river-crossing giant by
 * carrying a span over water, the cat-face junction by carrying three or
 * more lines, and the dead-end anchor by turning a through-line sharper
 * than 45°.
 */
export function towerRoles(puzzle: Puzzle, net: Network): NodeRole[] {
  const deg = Array<number>(net.nodes.length).fill(0);
  const overWater = Array<boolean>(net.nodes.length).fill(false);
  const nbrs: Point[][] = net.nodes.map(() => []);

  for (const e of net.edges) {
    deg[e.ai]++;
    deg[e.bi]++;
    nbrs[e.ai].push(e.b);
    nbrs[e.bi].push(e.a);
    if (spansWater(puzzle, e.a, e.b)) {
      overWater[e.ai] = true;
      overWater[e.bi] = true;
    }
  }
  for (const hl of net.houseLinks) {
    if (hl.nodeIdx != null) {
      deg[hl.nodeIdx]++;
      if (spansWater(puzzle, net.nodes[hl.nodeIdx], hl.h)) overWater[hl.nodeIdx] = true;
    }
  }

  return net.nodes.map((n, i) =>
    i === 0
      ? 'plant'
      : overWater[i]
        ? 'crossing'
        : deg[i] >= 3
          ? 'junction'
          : nbrs[i].length === 2 && sharpTurn(n, nbrs[i][0], nbrs[i][1])
            ? 'deadend'
            : 'standard',
  );
}
